// Shared "Style Cart" — localStorage-backed staging area for codes picked in the
// RC Code Book that should be auto-applied next time POS or Quick Order mounts.
//
// SECURITY: This cart is intentionally allowed in localStorage because it
// contains only **non-sensitive** factory codes — never customer PII,
// credentials, payment, or measurement data. The `safe()` guard below
// strips any unexpected fields before write so an XSS-injected mistake
// (or future refactor) can't accidentally widen the surface.
//
// Storage shape:
//   { items: [{ code: "C-0001", label: "Notch Lapel", field: "lapel_type", value: "Notch" }, ...] }
//
// POS and Quick Order read + clear this on mount via `consumeStyleCart()`.

const KEY = "rcStyleCart_v1";

// Whitelist of fields that may be persisted on a cart item. Anything else
// — phone, email, address, measurements, ssn, payment_token — is dropped
// before write, so a careless `addToStyleCart({ ...customer })` call
// cannot leak PII into localStorage.
const ALLOWED_ITEM_FIELDS = ["code", "label", "field", "value", "section", "garment"];

function safe(entry) {
  if (!entry || typeof entry !== "object") return null;
  const out = {};
  for (const k of ALLOWED_ITEM_FIELDS) {
    if (entry[k] !== undefined && typeof entry[k] !== "object") {
      out[k] = entry[k];
    }
  }
  return out.code ? out : null;
}

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return { items: [] };
    // Defence-in-depth: re-sanitise on read too in case storage was tampered with.
    return { items: parsed.items.map(safe).filter(Boolean) };
  } catch {
    return { items: [] };
  }
}

function write(cart) {
  try {
    const sanitised = { items: (cart.items || []).map(safe).filter(Boolean) };
    localStorage.setItem(KEY, JSON.stringify(sanitised));
    // Fire a custom event so any open tab updates its cart badge immediately
    window.dispatchEvent(new CustomEvent("rcStyleCart:update", { detail: sanitised }));
  } catch {
    /* quota errors are non-fatal */
  }
}

export function getStyleCart() {
  return read();
}

export function addToStyleCart(entry) {
  const item = safe(entry);
  if (!item) return;
  const cart = read();
  // De-dupe by field — clicking two lapel styles should keep the most recent
  cart.items = cart.items.filter((i) => i.field !== item.field);
  cart.items.push(item);
  write(cart);
}

export function removeFromStyleCart(code) {
  const cart = read();
  cart.items = cart.items.filter((i) => i.code !== code);
  write(cart);
}

export function clearStyleCart() {
  write({ items: [] });
}

// Used by POS / Quick Order on mount: returns the cart and clears it.
export function consumeStyleCart() {
  const cart = read();
  write({ items: [] });
  return cart;
}

export function subscribeStyleCart(handler) {
  const wrapped = (e) => handler(e.detail || read());
  window.addEventListener("rcStyleCart:update", wrapped);
  return () => window.removeEventListener("rcStyleCart:update", wrapped);
}
